import * as Sentry from "@sentry/react-native";
import Constants from "expo-constants";
import { AppState, Platform } from "react-native";
import type {
  GraphQLResponse as RelayGraphQLResponse,
  GraphQLSingularResponse,
  PayloadError,
} from "relay-runtime";

import { getStoredClubSlug, setStoredClubSlug } from "../clubSlugStorage";
import {
  CLUB_SLUG_STORAGE_KEY,
  SCHEMA_VERSION_HEADER_KEY,
  SCHEMA_VERSION_IS_OUTDATED_KEY,
  WHOOSH_SESSION_STORAGE_KEY,
  WHOOSH_SESSION_GENERATION_STORAGE_KEY,
  WHOOSH_SESSION_INVALIDATED_STORAGE_KEY,
  WHOOSH_SESSION_CREDENTIAL_CHANGED_EVENT,
} from "../constants";
import { handleErrors } from "../handleErrors";
import {
  attachRelayFetchContextToError,
  captureRelayFetchErrorIfUnhandled,
} from "../sentryErrorContext";
import { getNativeApiHost } from "../utils/getNativeApiHost";
import { getSchemaHash } from "../utils/getSchemaHash";
import { getWebApiHost } from "../utils/getWebApiHost";
import { requestGraphQL } from "./graphqlRequest";
import type {
  GraphQLHttpResponse,
  GraphQLRequestOptions,
} from "./graphqlRequest";
import {
  getNativeSessionKey,
  captureNativeSessionPersistenceGeneration,
  isNativeSessionSignedOut,
  persistNativeSessionKeyIfCurrent,
} from "./nativeSessionPersistence";

type FetchGraphQLParams = {
  text: unknown;
  requiredNativeSessionGeneration?: number;
  variables: unknown;
  schemaVersionMismatchHandler?: () => void;
  clubSlug?: string | null;
  useStoredClubSlug?: boolean;
  onError?: (error: unknown) => void;
};

type SessionResponseState = {
  authenticationStatus: string | null;
  currentSessionGeneration: string | null;
  responseOk: boolean;
  sessionGeneration: string | null;
  standardSessionKey: string | null;
  workstationSessionKey: string | null;
};

export const shouldClearInvalidatedSession = ({
  authenticationStatus,
  currentSessionGeneration,
  responseOk,
  sessionGeneration,
  standardSessionKey,
  workstationSessionKey,
}: SessionResponseState) =>
  responseOk &&
  authenticationStatus?.toUpperCase() === "AUTHENTICATED" &&
  !!standardSessionKey &&
  !workstationSessionKey &&
  sessionGeneration === currentSessionGeneration;

export const resolveRequestClubSlug = ({
  clubSlug,
  storedClubSlug,
  useStoredClubSlug = true,
}: {
  clubSlug?: string | null;
  storedClubSlug: string | null;
  useStoredClubSlug?: boolean;
}) =>
  clubSlug !== undefined ? clubSlug : useStoredClubSlug ? storedClubSlug : null;

async function fetchGraphQL({
  text,
  requiredNativeSessionGeneration,
  variables,
  schemaVersionMismatchHandler,
  clubSlug,
  useStoredClubSlug = true,
  onError,
}: FetchGraphQLParams): Promise<RelayGraphQLResponse> {
  let whooshKey: null | string = null;
  const nativeSessionPersistenceGeneration =
    captureNativeSessionPersistenceGeneration();

  if (Platform.OS !== "web") {
    whooshKey = await getNativeSessionKey();
  }
  const nativeSessionSignedOut =
    Platform.OS !== "web" && (await isNativeSessionSignedOut());
  const configDeployEnv = Constants?.expoConfig?.extra?.eas?.deployEnv;
  const isLocal =
    Platform.OS === "web"
      ? window.location.host.includes("localhost")
      : configDeployEnv === "local";

  const isE2E = configDeployEnv === "e2e";
  const configAPIBase = Constants?.expoConfig?.extra?.eas?.apiBase;

  const apiBase =
    Platform.OS !== "web"
      ? await getNativeApiHost({ isLocal, configAPIBase })
      : await getWebApiHost({ isE2E });

  const fetchOpts: GraphQLRequestOptions = {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      query: text,
      variables,
    }),
  };

  /**
   * We set an explicit club slug in async storage. When a caller does not
   * opt out, we use the previously set slug if no explicit slug is provided.
   *
   * This will allow us to remember the last club our user was in in the case of
   * a cold reboot.
   */
  // TODO(ENG-2770): Remove implicit context for direct callers after context rollout.
  const storedClubSlug =
    clubSlug === undefined && useStoredClubSlug
      ? await getStoredClubSlug()
      : null;
  const requestClubSlug = resolveRequestClubSlug({
    clubSlug,
    storedClubSlug,
    useStoredClubSlug,
  });

  if (!!clubSlug) {
    await setStoredClubSlug(clubSlug);
  }

  if (requestClubSlug) {
    fetchOpts.headers[CLUB_SLUG_STORAGE_KEY] = requestClubSlug;
  }

  if (!!whooshKey && Platform.OS !== "web" && !nativeSessionSignedOut) {
    fetchOpts.headers["x-whoosh-session-key"] = `${whooshKey}`;

    fetchOpts.credentials = "omit";
  } else if (Platform.OS !== "web") {
    fetchOpts.credentials = "omit";
  } else {
    fetchOpts.credentials = "include";
  }

  const schemaHash = await getSchemaHash();

  if (schemaHash) {
    fetchOpts.headers[SCHEMA_VERSION_HEADER_KEY] = `${schemaHash}`;
  }
  const url = `${isLocal || isE2E ? "" : "https://"}${apiBase}/private/api`;
  const sessionGeneration =
    Platform.OS === "web"
      ? localStorage.getItem(WHOOSH_SESSION_GENERATION_STORAGE_KEY)
      : null;
  let response: GraphQLHttpResponse | undefined;
  let responseBodyType: string | null = null;

  try {
    if (
      Platform.OS !== "web" &&
      requiredNativeSessionGeneration !== undefined
    ) {
      // Resolve the credential at dispatch, after asynchronous request setup.
      // Never turn an authenticated mutation into an anonymous request while
      // relocking, expiring, or switching accounts.
      const currentKey = await getNativeSessionKey();
      if (
        !currentKey ||
        AppState.currentState !== "active" ||
        requiredNativeSessionGeneration !==
          captureNativeSessionPersistenceGeneration()
      ) {
        return { errors: [{ message: "UNAUTHENTICATED" }] };
      }
      fetchOpts.headers["x-whoosh-session-key"] = currentKey;
    }
    response = await requestGraphQL(url, fetchOpts);

    // Get the response as JSON
    const res = await response.json();
    responseBodyType =
      res === null ? "null" : Array.isArray(res) ? "array" : typeof res;

    const workstationSessionKey = response.headers.get(
      "x-whoosh-workstation-session-key"
    );
    const standardSessionKey = response.headers.get("x-whoosh-session-key");
    const sessionKey = workstationSessionKey ?? standardSessionKey;
    const authenticationStatus = response.headers.get(
      "x-whoosh-authentication-status"
    );
    const currentSessionGeneration =
      Platform.OS === "web"
        ? localStorage.getItem(WHOOSH_SESSION_GENERATION_STORAGE_KEY)
        : null;

    if (
      Platform.OS === "web" &&
      shouldClearInvalidatedSession({
        authenticationStatus,
        currentSessionGeneration,
        responseOk: response.ok,
        sessionGeneration,
        standardSessionKey,
        workstationSessionKey,
      })
    ) {
      localStorage.removeItem(WHOOSH_SESSION_INVALIDATED_STORAGE_KEY);
    }

    const responseSchemaIsOutdated = response.headers.get(
      SCHEMA_VERSION_IS_OUTDATED_KEY
    );

    const shouldStoreSessionKey =
      Platform.OS !== "web" ||
      (sessionGeneration === currentSessionGeneration &&
        !localStorage.getItem(WHOOSH_SESSION_INVALIDATED_STORAGE_KEY));

    const currentSessionKey =
      Platform.OS === "web"
        ? localStorage.getItem(WHOOSH_SESSION_STORAGE_KEY)
        : whooshKey;

    if (
      sessionKey &&
      shouldStoreSessionKey &&
      sessionKey !== currentSessionKey
    ) {
      if (Platform.OS !== "web") {
        await persistNativeSessionKeyIfCurrent(
          sessionKey,
          nativeSessionPersistenceGeneration
        );
      } else {
        localStorage.setItem(WHOOSH_SESSION_STORAGE_KEY, sessionKey);
      }

      if (Platform.OS === "web" && workstationSessionKey) {
        window.dispatchEvent(
          new Event(WHOOSH_SESSION_CREDENTIAL_CHANGED_EVENT)
        );
      }
    }

    const schemaVersionMismatch =
      !!responseSchemaIsOutdated &&
      responseSchemaIsOutdated.toUpperCase() === "TRUE";

    const graphQLErrors = getGraphQLResponseErrors(res);
    const hasGraphQLErrors = graphQLErrors.length > 0;
    const hasRequestLevelError = !response.ok;

    if (
      schemaVersionMismatch &&
      (hasGraphQLErrors || hasRequestLevelError) &&
      schemaVersionMismatchHandler
    ) {
      schemaVersionMismatchHandler();
      Sentry.captureMessage("Schema Version Mismatch Triggered in Native App");

      return appendSchemaVersionMismatchError(res);
    }
    handleErrors(res, onError);

    return res;
  } catch (e) {
    attachRelayFetchContextToError(e, {
      url,
      method: fetchOpts.method,
      // Keep diagnostic fields flat so Sentry normalization preserves them.
      // Request headers, variables and response bodies can contain credentials or personal data.
      operationName:
        typeof text === "string"
          ? text
              .replace(/#[^\r\n]*/g, "")
              .match(
                /^\s*(?:query|mutation|subscription)\s+([_A-Za-z][_0-9A-Za-z]*)\b/
              )?.[1] ?? null
          : null,
      clubSlug: requestClubSlug,
      httpStatus: response?.status ?? null,
      responseContentType: response?.headers.get("content-type") ?? null,
      requestId: response?.headers.get("x-request-id") ?? null,
      responseBodyType,
    });
    captureRelayFetchErrorIfUnhandled(e, Sentry.captureException);

    throw e;
  }
}

const getGraphQLResponseErrors = (
  response: RelayGraphQLResponse
): PayloadError[] => {
  if (isGraphQLResponseArray(response)) {
    return Array.from(response).flatMap(getGraphQLSingularResponseErrors);
  }

  return getGraphQLSingularResponseErrors(response);
};

const getGraphQLSingularResponseErrors = (
  response: GraphQLSingularResponse
): PayloadError[] => {
  if ("errors" in response && Array.isArray(response.errors)) {
    return response.errors;
  }

  return [];
};

const appendSchemaVersionMismatchError = (
  response: RelayGraphQLResponse
): RelayGraphQLResponse => {
  if (isGraphQLResponseArray(response)) {
    return Array.from(response).map(
      appendSchemaVersionMismatchErrorToSingularResponse
    );
  }

  return appendSchemaVersionMismatchErrorToSingularResponse(response);
};

const appendSchemaVersionMismatchErrorToSingularResponse = (
  response: GraphQLSingularResponse
): GraphQLSingularResponse => {
  const errors = [
    ...getGraphQLSingularResponseErrors(response),
    { message: "Schema version mismatch" },
  ];

  if ("data" in response && response.data) {
    return {
      ...response,
      errors,
    };
  }

  return {
    errors,
  };
};

const isGraphQLResponseArray = (
  response: RelayGraphQLResponse
): response is readonly GraphQLSingularResponse[] => {
  return Array.isArray(response);
};

export default fetchGraphQL;
